import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppStore } from '@/stores/appStore';
import { useLightingEffects } from '@/contexts/LightingEffectsContext';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { StripLightingMarker } from '@/components/floor-plans/StripLightingMarker';
import type { LightingFixture } from '@/types/floorPlanViewer';
import { logger } from '@/lib/logger';

const GEOMETRY_PREVIEW_CLEAR_DELAY_MS = 1500;
const GEOMETRY_PREVIEW_EPSILON = 0.5;

type StripPoint = { x: number; y: number };

const getStripPoints = (strip: LightingFixture): StripPoint[] =>
  strip.strip_points && strip.strip_points.length > 1
    ? strip.strip_points
    : [
        { x: strip.x_position, y: strip.y_position },
        {
          x: strip.end_x_position ?? strip.x_position,
          y: strip.end_y_position ?? strip.y_position,
        },
      ];

const pointsMatch = (a: StripPoint | undefined, b: StripPoint | undefined) =>
  !!a &&
  !!b &&
  Math.abs(a.x - b.x) <= GEOMETRY_PREVIEW_EPSILON &&
  Math.abs(a.y - b.y) <= GEOMETRY_PREVIEW_EPSILON;

export interface StripLayerProps {
  /** All fixtures from the floor plan (will be filtered to linear only) */
  fixtures: LightingFixture[];
  /** Current zoom scale */
  scale: number;
  /** Handler to start dragging a strip (uses 'fixture' type since strips are fixtures) */
  onStartDrag?: (
    type: 'fixture',
    id: string,
    e: React.MouseEvent,
    position?: { x: number; y: number },
  ) => void;
  /** Whether currently dragging */
  isDragging?: boolean;
  /** Current drag state */
  dragState?: { type: string; id: string } | null;
  /** Function to check if element was recently dragged */
  wasRecentlyDragged?: (id: string) => boolean;
  /** Whether in public/readonly view */
  isPublicView?: boolean;
  /** Handler for endpoint resize */
  onEndpointDrag?: (stripId: string, endpoint: 'start' | 'end', x: number, y: number) => void;
  /** Handler for polyline vertex resize */
  onVertexDrag?: (stripId: string, index: number, x: number, y: number) => void;
  /** Convert screen coordinates to image coordinates (for endpoint drag) */
  screenToImageCoordinates?: (clientX: number, clientY: number) => { x: number; y: number };
}

/**
 * StripLayer renders all linear/strip fixtures and handles their interactions.
 * It filters fixtures to only show those with fixture_form_factor = 'linear'.
 */
export const StripLayer: React.FC<StripLayerProps> = ({
  fixtures,
  scale,
  onStartDrag,
  isDragging = false,
  dragState,
  wasRecentlyDragged,
  isPublicView = false,
  onEndpointDrag,
  onVertexDrag,
  screenToImageCoordinates,
}) => {
  // Force update mechanism for selection changes
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);

  // Store subscriptions
  const selectedCircuits = useAppStore(state => state.selection.circuits);
  const selectedFixtures = useAppStore(state => state.selection.fixtures);
  const selectedControls = useAppStore(state => state.selection.controls);
  const hoveredEntityId = useAppStore(state => state.ui.hoveredEntityId);
  const hoveredEntityType = useAppStore(state => state.ui.hoveredEntityType);
  const viewMode = useAppStore(state => state.ui.viewMode);
  const showOnlyUnassigned = useAppStore(state => state.ui.toolbar.showOnlyUnassigned);
  const markerOpacity = useAppStore(state => state.aiTools.markerOpacity);

  // Actions
  const selectEntity = useAppStore(state => state.selectEntity);
  const selectMultipleEntities = useAppStore(state => state.selectMultipleEntities);
  const setHoveredEntity = useAppStore(state => state.setHoveredEntity);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);

  // Lighting effects
  const { sim } = useLightingEffects();

  // Floor plan context for circuits and controls data
  const { data } = useFloorPlanContext();
  const circuitsData = data.circuits || [];
  const controlsData = data.controls || [];

  // State for endpoint dragging
  const [endpointDrag, setEndpointDrag] = useState<{
    stripId: string;
    endpoint: 'start' | 'end';
    startX: number;
    startY: number;
    /** The opposite endpoint position (for shift-constrain) */
    anchorPoint: { x: number; y: number } | null;
  } | null>(null);

  // Live position of the dragged endpoint (in image coordinates) for visual feedback
  const [draggedEndpointPos, setDraggedEndpointPos] = useState<{
    stripId: string;
    endpoint: 'start' | 'end';
    x: number;
    y: number;
    pendingCommit?: boolean;
  } | null>(null);
  const [vertexDrag, setVertexDrag] = useState<{
    stripId: string;
    index: number;
    startX: number;
    startY: number;
  } | null>(null);
  const [draggedVertexPos, setDraggedVertexPos] = useState<{
    stripId: string;
    index: number;
    x: number;
    y: number;
    pendingCommit?: boolean;
  } | null>(null);

  // Force update when selection changes
  useEffect(() => {
    const unsubscribe = useAppStore.subscribe(
      state => state.selection.circuits,
      () => forceUpdate(),
    );
    return unsubscribe;
  }, []);

  // Filter to only linear fixtures (strips)
  const strips = fixtures.filter(f => f.fixture_form_factor === 'linear');

  // Build set of circuits from selected controls (for blinking when control is selected)
  const selectedControlCircuits = useMemo(() => {
    const circuits = new Set<string>();
    if (selectedControls.size > 0) {
      controlsData.forEach((control: { id: string; circuit_id?: string | null }) => {
        if (selectedControls.has(control.id) && control.circuit_id) {
          circuits.add(control.circuit_id);
        }
      });
    }
    return circuits;
  }, [selectedControls, controlsData]);

  // Build set of circuits from selected fixtures (for blinking when another fixture in same circuit is selected)
  const selectedFixtureCircuits = useMemo(() => {
    const circuits = new Set<string>();
    if (selectedFixtures.size > 0) {
      fixtures.forEach(fixture => {
        if (selectedFixtures.has(fixture.id) && fixture.circuit_id) {
          circuits.add(fixture.circuit_id);
        }
      });
    }
    return circuits;
  }, [selectedFixtures, fixtures]);

  // Handle strip click
  const handleStripClick = useCallback(
    (stripId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (isPublicView) return;

      // Check if recently dragged
      if (wasRecentlyDragged && wasRecentlyDragged(stripId)) {
        logger.log('Blocked strip click - was recently dragged');
        return;
      }

      logger.log(`Strip ${stripId} clicked`);

      if (e.shiftKey && selectedFixtures.size > 0) {
        // Multi-select with shift
        const newSelection = new Set(selectedFixtures);
        newSelection.add(stripId);
        selectMultipleEntities('fixture', Array.from(newSelection));
      } else {
        // Single select
        selectEntity('fixture', stripId);
      }

      // Open fixture drawer (strips are fixtures)
      setActiveDrawer('fixture');
    },
    [
      selectedFixtures,
      selectEntity,
      selectMultipleEntities,
      setActiveDrawer,
      wasRecentlyDragged,
      isPublicView,
    ],
  );

  // Handle mouse enter for hover effects
  const handleMouseEnter = useCallback(
    (stripId: string) => {
      if (!isDragging && !isPublicView) {
        setHoveredEntity(stripId, 'fixture');
      }
    },
    [setHoveredEntity, isDragging, isPublicView],
  );

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    if (!isDragging && !isPublicView) {
      setHoveredEntity(null, null);
    }
  }, [setHoveredEntity, isDragging, isPublicView]);

  // Handle mouse down for drag
  const handleMouseDown = useCallback(
    (stripId: string, strip: LightingFixture, e: React.MouseEvent) => {
      if (e.button !== 0) return;
      if (isPublicView) return;
      // Don't start body drag while a geometry handle is being dragged
      if (endpointDrag || vertexDrag) return;

      e.preventDefault();
      e.stopPropagation();

      if (onStartDrag) {
        // Compute the wrapper div's top-left position (matches StripLightingMarker layout)
        const points = getStripPoints(strip);
        let minX = points[0].x,
          minY = points[0].y,
          maxX = points[0].x,
          maxY = points[0].y;
        for (const p of points) {
          minX = Math.min(minX, p.x);
          minY = Math.min(minY, p.y);
          maxX = Math.max(maxX, p.x);
          maxY = Math.max(maxY, p.y);
        }
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;
        const baseStrokeWidth = Math.max(3, 4 / scale);
        const strokeWidth = baseStrokeWidth * (strip.scale || 1.0);
        const padding = Math.max(20, strokeWidth * 4);
        const svgWidth = maxX - minX + padding * 2;
        const svgHeight = maxY - minY + padding * 2;
        const wrapperLeft = centerX - svgWidth / 2;
        const wrapperTop = centerY - svgHeight / 2;

        // Store old position in the DOM element for delta computation on drop
        const el = document.querySelector(`[data-fixture-id="${stripId}"]`) as HTMLElement;
        if (el) {
          el.dataset.isStrip = 'true';
          el.dataset.oldLeft = String(wrapperLeft);
          el.dataset.oldTop = String(wrapperTop);
        }

        onStartDrag('fixture', stripId, e, { x: wrapperLeft, y: wrapperTop });
      }
    },
    [onStartDrag, isPublicView, endpointDrag, vertexDrag, scale],
  );

  // Handle endpoint drag start
  const handleEndpointDragStart = useCallback(
    (stripId: string, endpoint: 'start' | 'end', e: React.MouseEvent) => {
      if (isPublicView) return;

      e.preventDefault();
      e.stopPropagation();

      // Compute anchor point (the opposite endpoint) at drag start
      const strip = strips.find(s => s.id === stripId);
      let anchorPoint: { x: number; y: number } | null = null;
      if (strip) {
        const pts = getStripPoints(strip);
        anchorPoint = endpoint === 'start' ? pts[pts.length - 1] : pts[0];
      }

      setEndpointDrag({
        stripId,
        endpoint,
        startX: e.clientX,
        startY: e.clientY,
        anchorPoint,
      });

      logger.log(`Started dragging ${endpoint} endpoint of strip ${stripId}`);
    },
    [isPublicView, strips],
  );

  const handleVertexDragStart = useCallback(
    (stripId: string, index: number, e: React.MouseEvent) => {
      if (isPublicView) return;

      e.preventDefault();
      e.stopPropagation();

      setVertexDrag({
        stripId,
        index,
        startX: e.clientX,
        startY: e.clientY,
      });

      logger.log(`Started dragging vertex ${index} of strip ${stripId}`);
    },
    [isPublicView],
  );

  // Handle global mouse move for endpoint dragging
  // Uses capture: true to intercept events BEFORE useDragHandler's capture-phase listeners
  // IMPORTANT: Only fire mutation on mouseUp to prevent overwhelming the mutation system
  // NOTE: Dependencies must NOT include values that change on every render (like `strips`)
  // because setDraggedEndpointPos triggers re-renders, which would reset finalX/finalY
  useEffect(() => {
    if (!endpointDrag) return;

    // Anchor point is pre-computed and stored in endpointDrag state
    const { anchorPoint } = endpointDrag;

    // Track final screen position for mouseUp (mutable ref-like variable)
    let finalX = endpointDrag.startX;
    let finalY = endpointDrag.startY;
    // Also track the last shift state
    let lastShiftKey = false;

    // Constrain point to horizontal or vertical relative to anchor
    const constrainToHV = (anchor: { x: number; y: number }, point: { x: number; y: number }) => {
      const dx = Math.abs(point.x - anchor.x);
      const dy = Math.abs(point.y - anchor.y);
      if (dx > dy) {
        return { x: point.x, y: anchor.y };
      } else {
        return { x: anchor.x, y: point.y };
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      finalX = e.clientX;
      finalY = e.clientY;
      lastShiftKey = e.shiftKey;

      // Update live visual feedback by converting screen coords to image coords
      if (screenToImageCoordinates) {
        let imageCoords = screenToImageCoordinates(e.clientX, e.clientY);

        // Shift key: constrain to horizontal/vertical relative to the opposite endpoint
        if (e.shiftKey && anchorPoint) {
          imageCoords = constrainToHV(anchorPoint, imageCoords);
        }

        setDraggedEndpointPos({
          stripId: endpointDrag.stripId,
          endpoint: endpointDrag.endpoint,
          x: imageCoords.x,
          y: imageCoords.y,
          pendingCommit: false,
        });
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      // Send image coordinates to the handler (shift constraint applied)
      if (onEndpointDrag && screenToImageCoordinates) {
        let imageCoords = screenToImageCoordinates(finalX, finalY);

        // Apply shift constraint to final position
        const shiftHeld = e.shiftKey || lastShiftKey;
        if (shiftHeld && anchorPoint) {
          imageCoords = constrainToHV(anchorPoint, imageCoords);
        }

        logger.log(
          `Endpoint drag completed: ${endpointDrag.endpoint} at (${imageCoords.x}, ${imageCoords.y})${shiftHeld ? ' [shift-constrained]' : ''}`,
        );
        setDraggedEndpointPos({
          stripId: endpointDrag.stripId,
          endpoint: endpointDrag.endpoint,
          x: imageCoords.x,
          y: imageCoords.y,
          pendingCommit: true,
        });
        onEndpointDrag(endpointDrag.stripId, endpointDrag.endpoint, imageCoords.x, imageCoords.y);
      } else {
        setDraggedEndpointPos(null);
      }
      setEndpointDrag(null);
    };

    // Use capture: true so these fire BEFORE useDragHandler's capture-phase listeners
    document.addEventListener('mousemove', handleMouseMove, { capture: true });
    document.addEventListener('mouseup', handleMouseUp, { capture: true });

    return () => {
      document.removeEventListener('mousemove', handleMouseMove, { capture: true } as any);
      document.removeEventListener('mouseup', handleMouseUp, { capture: true } as any);
    };
  }, [endpointDrag, onEndpointDrag, screenToImageCoordinates]);

  useEffect(() => {
    if (!vertexDrag) return;

    let finalX = vertexDrag.startX;
    let finalY = vertexDrag.startY;

    const handleMouseMove = (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      finalX = e.clientX;
      finalY = e.clientY;

      if (screenToImageCoordinates) {
        const imageCoords = screenToImageCoordinates(e.clientX, e.clientY);
        setDraggedVertexPos({
          stripId: vertexDrag.stripId,
          index: vertexDrag.index,
          x: imageCoords.x,
          y: imageCoords.y,
          pendingCommit: false,
        });
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (onVertexDrag && screenToImageCoordinates) {
        const imageCoords = screenToImageCoordinates(finalX, finalY);
        logger.log(
          `Vertex drag completed: ${vertexDrag.index} at (${imageCoords.x}, ${imageCoords.y})`,
        );
        setDraggedVertexPos({
          stripId: vertexDrag.stripId,
          index: vertexDrag.index,
          x: imageCoords.x,
          y: imageCoords.y,
          pendingCommit: true,
        });
        onVertexDrag(vertexDrag.stripId, vertexDrag.index, imageCoords.x, imageCoords.y);
      } else {
        setDraggedVertexPos(null);
      }

      setVertexDrag(null);
    };

    document.addEventListener('mousemove', handleMouseMove, { capture: true });
    document.addEventListener('mouseup', handleMouseUp, { capture: true });

    return () => {
      document.removeEventListener('mousemove', handleMouseMove, { capture: true } as any);
      document.removeEventListener('mouseup', handleMouseUp, { capture: true } as any);
    };
  }, [vertexDrag, onVertexDrag, screenToImageCoordinates]);

  useEffect(() => {
    if (!draggedEndpointPos?.pendingCommit) return;

    const strip = strips.find(item => item.id === draggedEndpointPos.stripId);
    const points = strip ? getStripPoints(strip) : [];
    const persistedPoint =
      draggedEndpointPos.endpoint === 'start' ? points[0] : points[points.length - 1];

    if (pointsMatch(persistedPoint, draggedEndpointPos)) {
      setDraggedEndpointPos(null);
      return;
    }

    const timeout = window.setTimeout(() => {
      setDraggedEndpointPos(current =>
        current?.stripId === draggedEndpointPos.stripId &&
        current?.endpoint === draggedEndpointPos.endpoint &&
        pointsMatch(current, draggedEndpointPos)
          ? null
          : current,
      );
    }, GEOMETRY_PREVIEW_CLEAR_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [draggedEndpointPos, strips]);

  useEffect(() => {
    if (!draggedVertexPos?.pendingCommit) return;

    const strip = strips.find(item => item.id === draggedVertexPos.stripId);
    const persistedPoint = strip ? getStripPoints(strip)[draggedVertexPos.index] : undefined;

    if (pointsMatch(persistedPoint, draggedVertexPos)) {
      setDraggedVertexPos(null);
      return;
    }

    const timeout = window.setTimeout(() => {
      setDraggedVertexPos(current =>
        current?.stripId === draggedVertexPos.stripId &&
        current?.index === draggedVertexPos.index &&
        pointsMatch(current, draggedVertexPos)
          ? null
          : current,
      );
    }, GEOMETRY_PREVIEW_CLEAR_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [draggedVertexPos, strips]);

  // Create unique key for force re-render (include all selection types that affect blinking)
  const layerKey = `strip-layer-${Array.from(selectedCircuits).join('-')}-${Array.from(selectedFixtures).join('-')}-${Array.from(selectedControls).join('-')}`;

  if (strips.length === 0) {
    return null;
  }

  return (
    <div key={layerKey} className="absolute inset-0 pointer-events-none">
      {strips.map(strip => {
        const isSelected = selectedFixtures.has(strip.id);
        const isHovered = hoveredEntityId === strip.id;
        const hasCircuit = !!strip.circuit_id;
        const isCircuitMode = viewMode === 'circuits';
        // Note: strips use 'fixture' type for drag since they are lighting fixtures
        const isBeingDragged = dragState?.type === 'fixture' && dragState?.id === strip.id;

        // Apply unassigned filter
        if (showOnlyUnassigned && hasCircuit) {
          return null;
        }

        // Find circuit data for color
        const circuit = strip.circuit_id
          ? circuitsData.find(
              (c: {
                id: string;
                sequence_number?: number;
                parent_circuit_id?: string | null;
                color?: string;
              }) => c.id === strip.circuit_id,
            )
          : null;

        // Check if strip is in selected circuit (direct circuit selection, or via selected fixture/control in same circuit)
        const isInSelectedCircuit = strip.circuit_id
          ? selectedCircuits.has(strip.circuit_id) ||
            selectedControlCircuits.has(strip.circuit_id) ||
            selectedFixtureCircuits.has(strip.circuit_id)
          : false;

        // Check if in hovered circuit
        const isInHoveredCircuit = !!(
          hoveredEntityType === 'circuit' &&
          hoveredEntityId &&
          strip.circuit_id === hoveredEntityId
        );

        // Lighting simulation logic
        const isSimLit = !!(
          sim.running &&
          strip.circuit_id &&
          sim.circuits.includes(strip.circuit_id)
        );
        const shouldDimDuringSimulation = sim.running && !isSimLit;

        // Render StripLightingMarker directly without wrapper div
        // The wrapper was causing a zero-size container at (0,0) that blocked clicks
        // StripLightingMarker handles its own positioning and events
        return (
          <StripLightingMarker
            key={`${strip.id}-${isInSelectedCircuit}-${isSimLit}`}
            strip={strip}
            isSelected={isSelected}
            isHovered={isHovered || isInHoveredCircuit}
            isSimLit={isSimLit}
            shouldDimDuringSimulation={shouldDimDuringSimulation}
            isCircuitMode={isCircuitMode}
            isBeingDragged={isBeingDragged}
            zoom={scale}
            circuitColor={circuit?.color}
            circuitSequenceNumber={circuit?.sequence_number}
            hasParentCircuit={!!circuit?.parent_circuit_id}
            isInSelectedCircuit={isInSelectedCircuit}
            isInHoveredCircuit={isInHoveredCircuit}
            markerOpacity={markerOpacity}
            circuitBadgeHighlighted={!sim.running && (isInSelectedCircuit || isInHoveredCircuit)}
            draggedEndpoint={
              draggedEndpointPos && draggedEndpointPos.stripId === strip.id
                ? {
                    endpoint: draggedEndpointPos.endpoint,
                    x: draggedEndpointPos.x,
                    y: draggedEndpointPos.y,
                  }
                : undefined
            }
            draggedVertex={
              draggedVertexPos && draggedVertexPos.stripId === strip.id
                ? {
                    index: draggedVertexPos.index,
                    x: draggedVertexPos.x,
                    y: draggedVertexPos.y,
                  }
                : undefined
            }
            onClick={e => handleStripClick(strip.id, e)}
            onMouseDown={e => handleMouseDown(strip.id, strip, e)}
            onMouseEnter={() => handleMouseEnter(strip.id)}
            onMouseLeave={handleMouseLeave}
            onEndpointDragStart={(endpoint, e) => handleEndpointDragStart(strip.id, endpoint, e)}
            onVertexDragStart={(index, e) => handleVertexDragStart(strip.id, index, e)}
          />
        );
      })}
    </div>
  );
};

StripLayer.displayName = 'StripLayer';

export default StripLayer;
